'use client';

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Edit2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';
import type { OnboardingFormConfig, FormAnswerValue, OnboardingAnswerEntry } from '@/types';
import {
  buildOnboardingEditFormConfig,
  buildRichOnboardingAnswers,
  getOrderedOnboardingAnswers,
  sanitizeOnboardingAnswers,
} from '@/lib/onboarding-answers';
import { OnboardingAnswerDisplay } from '@/components/profile/onboarding-answer-display';
import { FormRenderer } from '@/components/onboarding/form-renderer';
import axiosInstance from '@/lib/axios';
import { useDiscardChangesConfirm } from '@/hooks/use-discard-changes-confirm';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';

interface OnboardingAnswersReadOnlyProps {
  communityTitle: string;
  onboardingFormConfig: OnboardingFormConfig | null | undefined;
  onboardingAnswers?: Record<string, FormAnswerValue | OnboardingAnswerEntry> | null;
  /** When set with isEditable, enables editing via membership PATCH. */
  communityId?: string;
  isEditable?: boolean;
}

export function OnboardingAnswersReadOnly({
  communityTitle,
  onboardingFormConfig,
  onboardingAnswers,
  communityId,
  isEditable = false,
}: OnboardingAnswersReadOnlyProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { mutate } = useSWRConfig();

  const editFormConfig = useMemo(
    () => buildOnboardingEditFormConfig(onboardingFormConfig, onboardingAnswers),
    [onboardingFormConfig, onboardingAnswers]
  );

  const { requestDiscard, dialog: discardDialog } = useDiscardChangesConfirm({
    when: isEditOpen && isFormDirty,
  });
  const { dialog: navGuardDialog } = useUnsavedChangesGuard({
    when: isEditOpen && isFormDirty,
  });

  const closeEdit = () => {
    setIsEditOpen(false);
    setIsFormDirty(false);
  };

  if (
    !onboardingFormConfig?.steps?.length ||
    onboardingFormConfig.steps.every((s) => !s.fields?.length)
  ) {
    return null;
  }

  const canEdit = Boolean(isEditable && communityId);

  const orderedAnswers = getOrderedOnboardingAnswers(onboardingFormConfig, onboardingAnswers);
  const hasAnyAnswer = orderedAnswers.some((item) => !isEmpty(item.answer));

  const steps = onboardingFormConfig.steps.map((step) => ({
    ...step,
    answers: orderedAnswers.filter((item) => item.stepId === step.id),
  }));

  const legacyAnswers = orderedAnswers.filter((item) => item.stepId === 'legacy');

  const handleSubmit = async (answers: Record<string, FormAnswerValue>) => {
    if (!communityId || !editFormConfig) return;
    setIsSubmitting(true);
    try {
      const questionFallback = Object.fromEntries(
        legacyAnswers.map((item) => [item.field.id, item.question])
      );
      await axiosInstance.patch(`/api/communities/${communityId}/membership`, {
        onboardingAnswers: buildRichOnboardingAnswers(
          editFormConfig,
          sanitizeOnboardingAnswers(answers),
          questionFallback
        ),
      });
      toast.success('Additional details updated');
      closeEdit();
      await mutate('/api/user');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update answers');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {discardDialog}
      {navGuardDialog}
      <Card className="glass-card border-border/70 rounded-3xl shadow-xl overflow-hidden">
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base font-black font-display tracking-tight">
              {communityTitle}
            </CardTitle>
            <p className="text-xs text-muted-foreground font-medium">
              Additional details for this community
            </p>
          </div>
          {canEdit ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setIsEditOpen(true)}
              className="gap-1.5 rounded-xl h-8 px-3 text-xs font-bold border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/60 shrink-0"
            >
              <Edit2 className="h-3.5 w-3.5" />
              Edit
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          {hasAnyAnswer ? (
            <div className="space-y-6">
              {steps.map((step) =>
                step.answers.length > 0 ? (
                  <section key={step.id} className="space-y-3">
                    {onboardingFormConfig.steps.length > 1 && (
                      <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground px-1">
                        {step.title}
                      </h4>
                    )}
                    <div className="grid gap-3">
                      {step.answers.map(({ field, question, answer }) => (
                        <div
                          key={field.id}
                          className="flex flex-col gap-1.5 p-4 rounded-2xl bg-muted/35 dark:bg-muted/20 border border-border shadow-sm"
                        >
                          <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                            {question}
                          </span>
                          <OnboardingAnswerDisplay field={field} answer={answer} />
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null
              )}
              {legacyAnswers.length > 0 && (
                <section className="space-y-3">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground px-1">
                    Other
                  </h4>
                  <div className="grid gap-3">
                    {legacyAnswers.map(({ field, question, answer }) => (
                      <div
                        key={field.id}
                        className="flex flex-col gap-1.5 p-4 rounded-2xl bg-muted/35 dark:bg-muted/20 border border-border shadow-sm"
                      >
                        <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                          {question}
                        </span>
                        <OnboardingAnswerDisplay field={field} answer={answer} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
              <FileText className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No answers provided yet.</p>
              {canEdit ? (
                <Button
                  type="button"
                  variant="link"
                  className="mt-2 text-primary"
                  onClick={() => setIsEditOpen(true)}
                >
                  Add details
                </Button>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {canEdit ? (
        <Dialog
          open={isEditOpen}
          onOpenChange={(open) => {
            if (open) {
              setIsEditOpen(true);
              return;
            }
            requestDiscard(closeEdit);
          }}
        >
          <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden bg-background sm:max-h-[85vh]">
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="text-xl font-bold font-display tracking-tight">
                Edit additional details
              </DialogTitle>
              <p className="text-sm text-muted-foreground">{communityTitle}</p>
            </DialogHeader>
            <div className="p-6 pt-2 overflow-y-auto max-h-[60vh]">
              {isSubmitting ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : null}
              {editFormConfig ? (
                <FormRenderer
                  key={`${communityId}-edit-${isEditOpen}`}
                  config={editFormConfig}
                  onSubmit={handleSubmit}
                  onCancel={() => requestDiscard(closeEdit)}
                  isSubmitting={isSubmitting}
                  initialAnswers={onboardingAnswers || {}}
                  submitText="Save Changes"
                  onDirtyChange={setIsFormDirty}
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

function isEmpty(answer: FormAnswerValue | undefined): boolean {
  if (answer === undefined || answer === null) return true;
  if (typeof answer === 'string') return answer.trim() === '';
  if (Array.isArray(answer)) return answer.length === 0;
  return false;
}
